import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  formatDate,
  formatDateTime,
  formatDateLong,
  formatDateShort,
  formatDateShortWithYear,
  formatWeekdayShort,
  formatTime,
  formatTimeFromDate,
  setDisplayTimeZone,
} from '../utils/dateFormatting';

const DEFAULTS = { date_format: 'DD/MM/YYYY', time_format: '24h', locale: 'en-AU', timezone: null, depots_enabled: false, worker_job_email_enabled: false, docket_depot_times_enabled: false };

let _cache = null;
let _inflight = null;

export function invalidateTenantSettings() {
  _cache = null;
  _inflight = null;
}

/**
 * Centralized access to the tenant's date/time formatting preferences.
 * Fetches tenant settings once per session (module cache) and exposes bound
 * formatter helpers so components never call raw `.toLocaleDateString()`.
 */
export function useTenantSettings() {
  const { api, user } = useAuth();
  const [settings, setSettings] = useState(_cache || DEFAULTS);

  useEffect(() => {
    if (_cache) {
      setSettings(_cache);
      setDisplayTimeZone(_cache.timezone);
      return;
    }
    if (!user?.client_id || !api) return;
    if (!_inflight) {
      _inflight = api
        .get(`/tenants/${user.client_id}`)
        .then((res) => {
          _cache = {
            date_format: res.data?.date_format || DEFAULTS.date_format,
            time_format: res.data?.time_format || DEFAULTS.time_format,
            locale: res.data?.locale || DEFAULTS.locale,
            timezone: res.data?.timezone || DEFAULTS.timezone,
            depots_enabled: res.data?.depots_enabled ?? false,
            docket_depot_times_enabled: res.data?.docket_depot_times_enabled ?? false,
            worker_job_email_enabled: res.data?.worker_job_email_enabled ?? false,
          };
          setDisplayTimeZone(_cache.timezone);
          return _cache;
        })
        .catch(() => {
          _cache = { ...DEFAULTS };
          return _cache;
        });
    }
    let active = true;
    _inflight.then((s) => {
      if (active) setSettings({ ...s });
    });
    return () => {
      active = false;
    };
  }, [user?.client_id, api]);

  return {
    ...settings,
    fmtDate: (v) => formatDate(v, settings.date_format),
    fmtDateTime: (v) => formatDateTime(v, settings.date_format, settings.time_format),
    fmtDateLong: (v) => formatDateLong(v, settings.date_format),
    fmtDateShort: (v) => formatDateShort(v, settings.date_format),
    fmtDateShortWithYear: (v) => formatDateShortWithYear(v, settings.date_format),
    fmtWeekdayShort: (v) => formatWeekdayShort(v, settings.date_format),
    fmtTime: (v) => formatTime(v, settings.time_format),
    fmtTimeFromDate: (v) => formatTimeFromDate(v, settings.time_format),
  };
}

export default useTenantSettings;